import { FunctionComponent, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { HStack } from "@chakra-ui/react";
import { MultiValue } from "chakra-react-select";
import { cloneDeep, debounce, isEmpty, set } from "lodash";

// Components
import DataCard from "../../../shared/DataCard/DataCard";
import DetailRow from "../../../shared/DetailElement/DetailRow";
import DetailField from "../../../shared/DetailElement/DetailField";
import StickyFooter from "../../../shared/StickyFooter/StickyFooter";
import StepperFooter from "../../../shared/StickyFooter/footers/StepperFooter";
import DatePicker from "../../../shared/DetailElement/Elements/DatePicker";

// Hooks
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { ChakraSelectOption, Claim, GroupSelectOption } from "../../../utils/types";
import { adjustDatePickerOffset, convertUnixTimestampToUTCDate, toLocalISODateString } from "../../../utils/helpers";
import { getLampCase, searchMatchedCases } from "../../../utils/api/cases";
import { Case, CaseSearchResponse, GetLampCaseResponse } from "../../../utils/api/protos/messages/cases.message_pb";
import { getProvider, searchProviders } from "../../../utils/api/providers";
import { getPolicyIdByMemberId } from "../../../utils/api/members";
import { RpcError, StatusCode } from "grpc-web";
import { getStatesList } from "../../../utils/api/core";

// Context
import { useClaim } from "../../../context-providers/claims-provider";

// Protos
import { CheckForDuplicateClaimResponse, ClaimPerson } from "../../../utils/api/protos/messages/claims.message_pb";
import { State } from "../../../utils/api/protos/messages/core.message_pb";
import {
	Provider,
	ProvidersGetResponse,
	ProvidersSearchResponse
} from "../../../utils/api/protos/messages/providers.message_pb";
import CustomModal from "../../../shared/Modals/CustomModal";
import { checkForDuplicateClaim } from "../../../utils/api/claims";

interface BasicInfoProps {
	handleStepClick: (index: number) => void;
	currentStep: number;
	isAttorney: boolean;
}

enum DatePickerIdentifier {
	DateOfInitialConsultation = "dateOfInitialConsultation",
	FinalDateOfService = "finalDateOfService",
	ReceivedOn = "receivedOn"
}

/**
 * NewClaim child component to display the "Basic Information" step of new Claim creation
 * @param props typeof: BasicInfoProps
 * @returns ReactElement
 */
const BasicInfo: FunctionComponent<BasicInfoProps> = (props: BasicInfoProps): ReactElement => {
	const { handleStepClick, currentStep, isAttorney } = props;
	const {
		claim,
		claimCase,
		localClaim,
		setLocalClaim,
		setLgPolicyId,
		handleClaimUpsert,
		handleFetchCase,
		doesLegalMatterExist
	} = useClaim();

	// Refs
	const firstRender = useRef(true);

	// State
	const [dateOfInitialConsultation, setDateOfInitialConsultation] = useState<string>(
		claim?.dateOfInitialConsultation ? toLocalISODateString(claim.dateOfInitialConsultation) : ""
	);
	const [finalDateOfService, setFinalDateOfService] = useState<string>(
		claim?.finalDateOfService ? toLocalISODateString(claim.finalDateOfService) : ""
	);
	const [receivedOn, setReceivedOn] = useState<string>(claim?.receivedOn ? toLocalISODateString(claim.receivedOn) : "");
	const [isLoadingPolicy, setIsLoadingPolicy] = useState(false);
	const [isDupeModalOpen, setIsDupeModalOpen] = useState(false);

	// Case State
	const [caseSelectOptions, setCaseSelectOptions] = useState<Array<GroupSelectOption>>([]);
	const [caseSearchResults, setCaseSearchResults] = useState<Array<Case>>([]);
	const [selectedCaseOption, setSelectedCaseOption] = useState<ChakraSelectOption>();
	const [caseSearchTerm, setCaseSearchTerm] = useState<string>("");
	const [isCaseSearchLoading, setIsCaseSearchLoading] = useState<boolean>(false);
	const [selectedLegalMatterStateOption, setSelectedLegalMatterStateOption] = useState<ChakraSelectOption>();
	const [isFetchingLegalMatterState, setIsFetchingLegalMatterState] = useState<boolean>(false);

	// States
	const [statesList, setStatesList] = useState<Array<State>>([]);
	const [stateOptionList, setStateOptionList] = useState<Array<GroupSelectOption>>([]);
	const [stateFilter, setStateFilter] = useState<string>("");
	const [isStateLoading, setIsStateLoading] = useState<boolean>(false);

	// Provider State
	const [providerSelectOptions, setProviderSelectOptions] = useState<Array<GroupSelectOption>>([]);
	const [providerSearchResults, setProviderSearchResults] = useState<Array<Provider>>([]);
	const [selectedProviderOption, setSelectedProviderOption] = useState<ChakraSelectOption>();
	const [providerSearchTerm, setProviderSearchTerm] = useState<string>("");
	const [isProviderSearchLoading, setIsProviderSearchLoading] = useState<boolean>(false);

	// Hooks
	const toast = useClaimsToast();
	const debounceCaseSearchHandler = debounce((term: string) => {
		if (firstRender.current === false) {
			// Only trigger search when minimum length is satisfied
			if (term.length >= 3) handleCaseSearch(term);
		}
		// if (firstRender.current) firstRender.current = false;
	}, 1000);
	// Too much unintended behavior if we remove the dependency array and I'm tired of the
	// IDE screaming at me
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debounceCaseSearch = useCallback((term: string) => debounceCaseSearchHandler(term), []);
	const debounceProviderSearchHandler = debounce((term: string) => {
		if (firstRender.current === false) {
			if (term.length >= 3) handleProviderSearch(term);
		}
		if (firstRender.current) firstRender.current = false;
	}, 1000);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debounceProviderSearch = useCallback((term: string) => debounceProviderSearchHandler(term), []);

	/**
	 * Determines whether the Next button is disabled
	 * IE - whether the form is valid or not
	 * @returns boolean
	 */
	const isNextDisabled = (): boolean => {
		return (
			!localClaim.atty ||
			!localClaim.member ||
			!localClaim.legalMatterState ||
			isEmpty(localClaim.finalDateOfService.toString()) ||
			isEmpty(localClaim.dateOfInitialConsultation.toString()) ||
			// Disable if an internal user is missing a received on date
			(!localClaim.receivedOn && !receivedOn && !isAttorney)
		);
	};

	/**
	 * Helper function to format the displayed text for Case Search Results
	 * @param selectedCase The specified Case of which to format
	 * @returns String in the format of "[caseId] - memberFirstName memberLastName"
	 */
	const formatCaseDisplay = (selectedCase: Case): string =>
		`${selectedCase.getClientnamefirst()} ${selectedCase.getClientnamelast()} [PASS: ${selectedCase.getCaseid()} / LAMP: ${selectedCase.getLampcaseid()}]`;

	/**
	 * Helper function to format the displayed text for Provider Search Results
	 * @param provider The specified Provider of which to format
	 * @returns String in the format of "[attyId] - firstName lastName"
	 */
	const formatProviderDisplay = (provider: Provider): string =>
		`[${provider.getAttyid()}] ${provider.getNamefirst()} ${provider.getNamelast()}`;

	/**
	 * Helper function to format the displayed text for Provider Search Results
	 * @param provider The specified Provider of which to format
	 * @returns String in the format of "[attyId] - firstName lastName"
	 */
	const formatSelectedProviderDisplay = (attyId: number, name: string): string => `[${attyId}] ${name}`;

	/**
	 * Helper function to process API returned Cases into Selectable options
	 * @param cases API returned list of Cases
	 */
	const processCasesForSelect = (cases: Array<Case>): void => {
		const selectOptionList: Array<GroupSelectOption> = [];

		// Do not process empty list
		if (cases.length > 0) {
			const caseOptions: Array<ChakraSelectOption> = cases.map((rawCase: Case) => {
				const caseOption: ChakraSelectOption = {
					// Display Format: [caseId] - firstName lastName
					label: formatCaseDisplay(rawCase),
					// CaseID is the source of truth!
					value: rawCase.getCaseid().toString()
				};

				return caseOption;
			});

			selectOptionList.push({
				label: "Case Details",
				value: "",
				options: caseOptions
			});
		}

		setCaseSelectOptions(selectOptionList);
	};

	/**
	 * Helper function to process API returned Providers into Selectable options
	 * @param providers API returned list of Providers
	 */
	const processProvidersForSelect = (providers: Array<Provider>): void => {
		const selectOptionList: Array<GroupSelectOption> = [];

		if (providers.length > 0) {
			const providerOptions: Array<ChakraSelectOption> = providers.map((rawProvider: Provider) => {
				const providerOption: ChakraSelectOption = {
					label: formatProviderDisplay(rawProvider),
					value: rawProvider.getAttyid().toString()
				};

				return providerOption;
			});

			selectOptionList.push({
				label: "Provider Details",
				value: "",
				options: providerOptions
			});
		}

		setProviderSelectOptions(selectOptionList);
	};

	const processUSStatesForSelect = (filter: string): void => {
		if (isStateLoading) return;

		const selectOptionList: Array<GroupSelectOption> = [];

		if (statesList.length > 0) {
			const stateOptions: Array<ChakraSelectOption> = statesList
				.filter((state) => {
					return (
						state.getName().toLowerCase().includes(filter.toLowerCase()) ||
						state.getCode().toLowerCase().includes(filter.toLowerCase())
					);
				})
				.map((state) => {
					const stateOption: ChakraSelectOption = {
						label: state.getName(),
						value: state.getCode()
					};
					return stateOption;
				});

			selectOptionList.push({
				label: "States",
				value: "",
				options: stateOptions
			});
		}

		setStateOptionList(selectOptionList);
	};

	const handleNextClick = async () => {
		// Should be non-null by next button disable logic
		if (localClaim?.member) {
			// Load the member's policy and save policy id, otherwise show an error
			setIsLoadingPolicy(true);
			try {
				const result = await getPolicyIdByMemberId(
					localClaim.member.getId(),
					localClaim.dateOfInitialConsultation,
					localClaim.finalDateOfService,
					localClaim.caseId
				);
				setLgPolicyId(result.getLgPolicyId());

				if (localClaim.dateOfInitialConsultation < convertUnixTimestampToUTCDate(result.getEffectiveDate())) {
					toast("Warning", "Date of Initial Consultation falls before the Effective Policy Date", "warning");
				}

				if (localClaim.caseId) {
					const dupeResult: CheckForDuplicateClaimResponse = await checkForDuplicateClaim(localClaim.caseId);
					if (!dupeResult.getIsDupe()) {
						let isSuccess = await handleClaimUpsert(localClaim, false);
						setIsLoadingPolicy(false);
						if (isSuccess) handleStepClick(currentStep + 1);
					} else {
						setIsDupeModalOpen(true);
					}
				} else {
					let isSuccess = await handleClaimUpsert(localClaim, false);
					setIsLoadingPolicy(false);
					if (isSuccess) handleStepClick(currentStep + 1);
				}
			} catch (err) {
				const rpcErr: RpcError = err as RpcError;
				toast("Error", rpcErr.message, "error");
				setIsLoadingPolicy(false);
			}
		}
	};

	const handleDupeModalConfirm = async () => {
		try {
			let isSuccess = await handleClaimUpsert(localClaim, false);
			setIsLoadingPolicy(false);
			if (isSuccess) handleStepClick(currentStep + 1);
		} catch (err) {
			const rpcErr: RpcError = err as RpcError;
			toast("Error", rpcErr.message, "error");
			setIsLoadingPolicy(false);
		}
	};

	const handleDateBlur = (propName: DatePickerIdentifier): void => {
		try {
			let newDate: Date = new Date();

			if (propName === DatePickerIdentifier.DateOfInitialConsultation) {
				newDate = new Date(dateOfInitialConsultation);
			} else if (propName === DatePickerIdentifier.FinalDateOfService) {
				newDate = new Date(finalDateOfService);
			} else if (propName === DatePickerIdentifier.ReceivedOn) {
				newDate = new Date(receivedOn);
			} else {
				console.warn(`Attempting to parse date for unknown property: ${propName}`);
			}

			let workingCopyClone: Claim = cloneDeep(localClaim);
			workingCopyClone = set(workingCopyClone, propName, adjustDatePickerOffset(newDate));
			setLocalClaim(workingCopyClone);
		} catch (err) {
			console.error(err);
			toast("Error", "date parsing failed", "error");

			setDateOfInitialConsultation(toLocalISODateString(localClaim.dateOfInitialConsultation));
			setFinalDateOfService(toLocalISODateString(localClaim.finalDateOfService));
		}
	};

	/**
	 * Handles making the API call to BFF
	 * @param term Search term with which the User is searching
	 */
	const handleCaseSearch = async (term: string): Promise<void> => {
		setIsCaseSearchLoading(true);
		await searchMatchedCases(term, localClaim.atty?.getId())
			.then((response: CaseSearchResponse) => {
				const caseList: Array<Case> = response.getCasesList();
				// Process the Select list
				processCasesForSelect(caseList);
				// Save the results for later processing
				setCaseSearchResults(caseList);
			})
			.catch((err) => {
				toast("Error", err.message, "error");
			})
			.finally(() => {
				setIsCaseSearchLoading(false);
			});
	};

	const handleFetchClaimCase = async (caseId: number): Promise<Case | undefined> => {
		setIsCaseSearchLoading(true);

		return handleFetchCase(caseId).finally(() => {
			setIsCaseSearchLoading(false);
		});
	};

	/**
	 * Handles making the API call to the BFF
	 * @param term Search term with which the User is searching
	 */
	const handleProviderSearch = async (term: string): Promise<void> => {
		setIsProviderSearchLoading(true);
		await searchProviders(term)
			.then((response: ProvidersSearchResponse) => {
				const providerList: Array<Provider> = response.getProvidersList();
				processProvidersForSelect(providerList);
				setProviderSearchResults(providerList);
			})
			.catch((err) => {
				toast("Error", err.message, "error");
			})
			.finally(() => {
				setIsProviderSearchLoading(false);
			});
	};

	const handleFetchProvider = async (providerId: number): Promise<Provider | undefined> => {
		setIsProviderSearchLoading(true);

		return getProvider(providerId)
			.then((response: ProvidersGetResponse) => {
				const foundProvider: Provider | undefined = response.getProvider();

				return foundProvider;
			})
			.catch((err: RpcError) => {
				console.error(err);
				if (err.code === StatusCode.NOT_FOUND) {
					toast(
						"Error",
						"The selected Attorney was not found. Please have Network Management add the Attorney to LAMP™",
						"error"
					);
				} else toast("Error", (err as RpcError).message, "error");

				return undefined;
			})
			.finally(() => {
				setIsProviderSearchLoading(false);
			});
	};

	const handleFetchProviderAndEffects = async (attyId: number | undefined): Promise<void> => {
		if (!attyId) return;

		const foundAttorney: Provider | undefined = await handleFetchProvider(attyId);
		if (foundAttorney) {
			processProvidersForSelect([foundAttorney]);
			setProviderSearchResults([foundAttorney]);
			const selectedProvider: ChakraSelectOption = {
				label: formatProviderDisplay(foundAttorney),
				value: foundAttorney.getAttyid().toString()
			};
			setSelectedProviderOption(selectedProvider);
		}
	};

	const handleFetchCaseAndEffects = async (caseId: number): Promise<void> => {
		const foundCase: Case | undefined = await handleFetchClaimCase(caseId);
		if (foundCase) {
			processCasesForSelect([foundCase]);
			setCaseSearchResults([foundCase]);
			const selectedCase: ChakraSelectOption = {
				label: formatCaseDisplay(foundCase),
				value: foundCase.getCaseid().toString()
			};
			setSelectedCaseOption(selectedCase);

			const claimAttyId: number | undefined = localClaim.atty?.getId();

			if (claimAttyId) await handleFetchProviderAndEffects(claimAttyId);
			else console.warn("Somehow processed a Claim with no Provider!");
		}
	};

	const handleFetchLegalMatterStateAndEffects = async (caseId: number): Promise<State | undefined> => {
		if (!caseId) {
			toast("No Lamp Case associated with this Case", "Please select Legal Jurisdiction", "warning");
			return undefined;
		}

		setIsFetchingLegalMatterState(true);
		let state: State | undefined;
		try {
			const response: GetLampCaseResponse = await getLampCase(caseId);

			const foundLampCase = response.getLampCase();
			if (foundLampCase) {
				state = foundLampCase.getLegalmatterstate();
				if (state) {
					const selectedStateOption: ChakraSelectOption = {
						label: state.getName(),
						value: state.getCode()
					};
					setSelectedLegalMatterStateOption(selectedStateOption);
				}
			}
		} catch (err) {
			const rpcErr: RpcError = err as RpcError;
			toast("Error", rpcErr.message, "error");
		} finally {
			setIsFetchingLegalMatterState(false);
		}
		return state;
	};

	/**
	 * Handles logic when the User selects a value from the searchable Select field
	 * @param event typeof: MultiValue<ChakraSelectOption>
	 * @param propertyName Which element is this relevant to?
	 */
	const handleMultiChange = async (event: MultiValue<ChakraSelectOption>, propertyName: string): Promise<void> => {
		setLocalClaim((prev: Claim) => {
			const updated: Claim = { ...prev };

			if (propertyName.toLowerCase() === "caseid") {
				// Check if the event is null
				// This happens when an option is removed
				if (event.length > 0) {
					// Find the relevant record from the search results
					// We need to update multiple fields
					const foundCases: Array<Case> = caseSearchResults.filter((searchResult: Case) => {
						return searchResult.getCaseid() === parseInt(event[0].value);
					});

					// If there was a match, there should only be one
					if (foundCases.length > 0) {
						const foundCase = foundCases[0];

						// Set MemberID, CaseID, LampCaseID, and LegalMatterState
						if (!updated.member) updated.member = new ClaimPerson();
						updated.member.setId(foundCase.getMemberid());
						updated.member.setName(`${foundCase.getClientnamefirst()} ${foundCase.getClientnamelast()}`);
						updated.caseId = foundCase.getCaseid();
						updated.lampCaseId = foundCase.getLampcaseid();

						// Save the selected option so we can display it properly
						const selectedCase: ChakraSelectOption = {
							label: formatCaseDisplay(foundCase),
							value: foundCase.getCaseid().toString()
						};
						setSelectedCaseOption(selectedCase);

						// Always update the attorney when a case is selected
						if (!updated.atty) updated.atty = new ClaimPerson();
						updated.atty.setId(foundCase.getAttyid());
						updated.atty.setName(foundCase.getProvidername());

						// Async updates for provider and legal matter state
						(async () => {
							const results = await Promise.all([
								handleFetchProviderAndEffects(updated?.atty?.getId()),
								handleFetchLegalMatterStateAndEffects(foundCase.getLampcaseid())
							]);
							const fetchedState: State | undefined = results[1];
							if (fetchedState) {
								setLocalClaim((prev2) => {
									const updated2 = { ...prev2, legalMatterState: fetchedState };
									return updated2;
								});
							}
						})();
					}
				} else {
					// Clear data if empty
					setSelectedLegalMatterStateOption(undefined);
					setSelectedCaseOption(undefined);
					updated.member = undefined;
					updated.caseId = undefined;
					setCaseSelectOptions([]); // Only clear options when explicitly clearing the selection
				}
			} else if (propertyName.toLowerCase() === "attyid") {
				if (event.length > 0) {
					const foundProviders: Array<Provider> = providerSearchResults.filter((searchResult: Provider) => {
						return searchResult.getAttyid() === parseInt(event[0].value);
					});
					if (foundProviders.length > 0) {
						const foundProvider = foundProviders[0];
						if (!updated.atty) updated.atty = new ClaimPerson();
						updated.atty.setId(foundProvider.getAttyid());
						updated.atty.setName(`${foundProvider.getNamefirst()} ${foundProvider.getNamelast()}`);

						const selectedProvider: ChakraSelectOption = {
							label: formatProviderDisplay(foundProvider),
							value: foundProvider.getAttyid().toString()
						};
						setSelectedProviderOption(selectedProvider);
					}
				} else {
					setSelectedProviderOption(undefined);
					updated.atty = undefined;
					setProviderSelectOptions([]);
				}
			} else if (propertyName.toLowerCase() === "legalmatterstate") {
				if (event.length > 0) {
					const state: State = statesList.find((state) => state.getCode() === event[0].value)!;
					updated.legalMatterState = state;
					const selectedLegalMatterState = {
						label: state?.getName() ?? "",
						value: event[0].value
					};
					setSelectedLegalMatterStateOption(selectedLegalMatterState);
				} else {
					setSelectedLegalMatterStateOption(undefined);
					updated.legalMatterState = undefined;
				}
			}

			return updated;
		});
	};

	useEffect(() => {
		debounceCaseSearch(caseSearchTerm);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [caseSearchTerm]);

	useEffect(() => {
		debounceProviderSearch(providerSearchTerm);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [providerSearchTerm]);

	useEffect(() => {
		if (localClaim.claimId) {
			if (localClaim.caseId) handleFetchCaseAndEffects(localClaim.caseId);
			else if (localClaim.atty) handleFetchProviderAndEffects(localClaim.atty.getId());
		} else {
			// Initialize initial and final dates
			let workingCopyClone: Claim = cloneDeep(localClaim);
			workingCopyClone = set(
				workingCopyClone,
				DatePickerIdentifier.DateOfInitialConsultation,
				adjustDatePickerOffset(localClaim.dateOfInitialConsultation)
			);
			workingCopyClone = set(
				workingCopyClone,
				DatePickerIdentifier.FinalDateOfService,
				adjustDatePickerOffset(localClaim.finalDateOfService)
			);
			setLocalClaim(workingCopyClone);
		}

		//Initialize States list
		setIsStateLoading(true);
		getStatesList()
			.then((response) => {
				setStatesList(response.getStatesList());
			})
			.finally(() => {
				setIsStateLoading(false);
			});
	}, []);

	/** If the local claim has a member or attorney or legalMatterState, select them */
	useEffect(() => {
		if (localClaim.atty) {
			const selectedProvider: ChakraSelectOption = {
				label: formatSelectedProviderDisplay(localClaim.atty.getId(), localClaim.atty.getName()),
				value: localClaim.atty.getId().toString()
			};
			setSelectedProviderOption(selectedProvider);
		}
		if (localClaim.member && claimCase) {
			const selectedCase: ChakraSelectOption = {
				label: formatCaseDisplay(claimCase),
				value: claimCase.getCaseid().toString()
			};
			setSelectedCaseOption(selectedCase);
		}

		if (localClaim.legalMatterState) {
			//Find the state object by code

			const selectedLegalMatterState: ChakraSelectOption = {
				label: localClaim.legalMatterState?.getName() ?? "",
				value: localClaim.legalMatterState?.getCode() ?? ""
			};
			setSelectedLegalMatterStateOption(selectedLegalMatterState);
		}
	}, [localClaim]);

	useEffect(() => {
		processUSStatesForSelect(stateFilter);
	}, [stateFilter, statesList]);

	return (
		<>
			<CustomModal
				title="Duplicate Claim"
				isOpen={isDupeModalOpen}
				size="3xl"
				message="Another Claim already exists for this PASS Case ID. Would you still like to proceed?"
				continueText="Continue"
				onContinue={() => handleDupeModalConfirm()}
				onCancel={() => {
					setIsDupeModalOpen(false);
					setIsLoadingPolicy(false);
				}}
			/>
			<DetailRow label="Member/Case">
				<DetailField
					type="chakra-select"
					isDeleteable={true}
					isEdit={true}
					selectedOptions={
						selectedCaseOption != null
							? [{ label: selectedCaseOption?.label ?? "", value: selectedCaseOption?.value ?? "" }]
							: []
					}
					isLoading={isCaseSearchLoading}
					options={caseSelectOptions}
					displayValue={localClaim?.caseId ?? ""}
					onMultiChange={(event) => handleMultiChange(event, "caseId")}
					onTextChange={(event) => setCaseSearchTerm(event.target.value)}
					allowNew={false}
					isDisabled={isAttorney}
					placeholder=""
				/>
			</DetailRow>
			<DetailRow label="Attorney">
				<DetailField
					type="chakra-select"
					isDeleteable={true}
					isEdit={true}
					selectedOptions={
						selectedProviderOption != null
							? [{ label: selectedProviderOption?.label ?? "", value: selectedProviderOption?.value ?? "" }]
							: []
					}
					isLoading={isProviderSearchLoading}
					options={providerSelectOptions}
					displayValue={localClaim?.atty?.getName() ?? ""}
					onMultiChange={(event) => handleMultiChange(event, "attyId")}
					onTextChange={(event) => setProviderSearchTerm(event.target.value)}
					allowNew={false}
					isDisabled={isAttorney}
					placeholder=""
				/>
			</DetailRow>
			<DetailRow label="Legal Jurisdiction">
				<DetailField
					type="chakra-select"
					isDeleteable={true}
					isEdit={true}
					selectedOptions={
						selectedLegalMatterStateOption != null
							? [
									{
										label: selectedLegalMatterStateOption?.label ?? "",
										value: selectedLegalMatterStateOption?.value ?? ""
									}
								]
							: []
					}
					isLoading={isStateLoading || isFetchingLegalMatterState}
					options={stateOptionList}
					displayValue={localClaim?.legalMatterState?.getName() ?? ""}
					onMultiChange={(event) => handleMultiChange(event, "legalMatterState")}
					onTextChange={(event) => setStateFilter(event.target.value)}
					allowNew={false}
					isDisabled={isAttorney && doesLegalMatterExist}
					placeholder=""
				/>
			</DetailRow>
			<DetailRow label="Date of Initial Consultation" flex={0}>
				<DatePicker
					value={dateOfInitialConsultation}
					max={toLocalISODateString(new Date())}
					onDateChange={(newDate) => setDateOfInitialConsultation(newDate)}
					onDateBlur={() => handleDateBlur(DatePickerIdentifier.DateOfInitialConsultation)}
				/>
			</DetailRow>
			<DetailRow label="Final Date of Service" flex={0}>
				<DatePicker
					value={finalDateOfService}
					max={toLocalISODateString(new Date())}
					onDateChange={(newDate) => setFinalDateOfService(newDate)}
					onDateBlur={() => handleDateBlur(DatePickerIdentifier.FinalDateOfService)}
				/>
			</DetailRow>
			{!isAttorney && (
				<DetailRow label="Received On" flex={0}>
					<DatePicker
						value={receivedOn}
						min="2025-10-02"
						max={toLocalISODateString(new Date())}
						onDateChange={(newDate) => setReceivedOn(newDate)}
						onDateBlur={() => handleDateBlur(DatePickerIdentifier.ReceivedOn)}
					/>
				</DetailRow>
			)}
			<StepperFooter
				hasPreviousStep={false}
				hasNextStep={true}
				isNextDisabled={isNextDisabled()}
				isNextLoading={isLoadingPolicy}
				isSubmit={false}
				onNext={() => handleNextClick()}
			/>
		</>
	);
};

export default BasicInfo;
